import React, { useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import MultiSelect from "./MultiSelect";
import SingleSelect from "./SingleSelect";
import DateRangePicker from "./DateRangePicker";
import { Icons } from "assets/icons";

const FilterBar = ({
  keyword,
  setKeyword,
  keywordPlaceholder = "Search",
  selectedFilters = {},
  setSelectedFilters = () => {},
  dates = [],
  setDates = () => {},
  filters = [],
  onSearch = () => {},
  onReset = () => {},
  className = "",
  isDateRange = true,
  isBtn = false,
  btnText = "",
  onBtnClick = () => {},
  onFilterUpdate = () => {},
  searchMaxWidth = 300,
  rightActions = null,
  isSearchEnabled = true,
}) => {
  const { t } = useTranslation();

  const { refresh, lightSearch, plus } = Icons || {};
  const searchDebounceRef = useRef(null);
  // Kept fresh on every render so deferred calls (setTimeout) always use the
  // latest onSearch (which closes over the latest redux-backed filter values),
  // instead of the stale onSearch captured when the timeout was scheduled.
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  const handleFilterChange = (key, value) => {
    setSelectedFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
    }));
    onFilterUpdate(key, value);
    // Apply filter immediately on selection
    setTimeout(() => {
      onSearchRef.current(1, { actionType: "SEARCH" });
    }, 0);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setKeyword(value);

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Call search when input becomes empty
    if (value.trim() === "") {
      searchDebounceRef.current = setTimeout(() => {
        onSearchRef.current(null, { actionType: "RESET_KEYWORD" });
      }, 100);
      return;
    }

    // Auto-apply search as the user types
    searchDebounceRef.current = setTimeout(() => {
      onSearchRef.current(1, { actionType: "SEARCH" });
    }, 400);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      onSearchRef.current(1, { actionType: "SEARCH" });
    }
  };

  const handleReset = () => {
    onReset();
  };

  const handleDatesChange = (value) => {
    setDates(value);

    const isRangeComplete = Array.isArray(value) && value[0] && value[1];
    if (isRangeComplete) {
      setTimeout(() => {
        onSearchRef.current(1, { actionType: "SEARCH" });
      }, 0);
    }
  };

  return (
    <Row className={`common_filter ${className}`}>
      {isSearchEnabled && (
        <Col className="filter_search_col" style={{ maxWidth: searchMaxWidth }}>
          <div className="search_input">
            <input
              type="text"
              placeholder={t(keywordPlaceholder)}
              value={keyword}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              title={keywordPlaceholder}
            />
            <img
              src={lightSearch}
              width={20}
              height={20}
              alt="search"
              className="search_icon"
            />
          </div>
        </Col>
      )}
      <Col>
        <div className="g_end flex-wrap gap-3 mt-2  mt-lg-0 filters_right">
          {filters.map((filter) => {
            const uniqueKey = `${filter.type}-${filter.key}`;
            const translatedOptions = filter.options?.map((option) => ({
              ...option,
              name: t(option.name),
            }));
            return filter.type === "multiSelect" ? (
              <MultiSelect
                key={filter.key}
                value={selectedFilters[filter.key] || []}
                options={translatedOptions}
                placeholder={filter.placeholder || t("Select Options")}
                onChange={(val) => handleFilterChange(filter.key, val)}
                hideError={true}
                className={uniqueKey}
              />
            ) : filter.type === "singleSelect" ? (
              <SingleSelect
                key={filter.key}
                value={selectedFilters[filter.key]}
                handleSingleSelectChange={(e) =>
                  handleFilterChange(filter.key, e.value)
                }
                options={translatedOptions}
                optionLabel="name"
                placeholder={filter.placeholder}
                className={`custom_dropdown ${uniqueKey}`}
                hideError={true}
                filter={filter.filter}
                filterPlaceholder={t(filter.filterPlaceholder || "Search")}
              />
            ) : filter.type === "text" ? (
              <input
                key={filter.key}
                type="text"
                className={`form-control filter-text ${uniqueKey}`}
                value={selectedFilters[filter.key] || ""}
                placeholder={filter.placeholder}
                onChange={(event) =>
                  handleFilterChange(filter.key, event.target.value)
                }
              />
            ) : (
              ""
            );
          })}

          {isDateRange && (
            <DateRangePicker dates={dates} setDates={handleDatesChange} />
          )}

          {rightActions && (
            <div className="custom-actions g_center gap-2 ms-2">
              {rightActions}
            </div>
          )}

          <div className="buttons">
            <button className="reset_btn" type="button" onClick={handleReset}>
              <img src={refresh} width={20} height={20} alt="search" />
            </button>
          </div>

          {isBtn && (
            <button
              type="button"
              className="main_btn g_center gap-1"
              onClick={onBtnClick}
            >
              <img src={plus} alt="" width={20} height={20} />
              {btnText}
            </button>
          )}
        </div>
      </Col>
    </Row>
  );
};

export default FilterBar;
